import { observer } from 'mobx-react-lite';
import { FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, useState } from 'react';
import styles from '@components/Input/Input.styles';
import InputBottomDescription from '@components/InputBottomDescription/InputBottomDescription';
type InternalInputType = {
  field: FieldState<string | null> | FieldState<string>;
  placeholder: string;
  disabled: boolean;
  focused: boolean;
  focusIn: () => void;
  focusOut: () => void;
  maxLength: number;
  cy?: string;
  ariaInvalid?: boolean;
  ariaErrorMessage?: string;
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  required?: boolean;
};
const InternalInput = observer(({
  field,
  placeholder,
  disabled,
  focused,
  focusIn,
  focusOut,
  maxLength,
  cy = '',
  ariaInvalid,
  ariaErrorMessage,
  id,
  ariaLabel,
  ariaDescribedBy,
  required = false
}: InternalInputType): JSX.Element => {
  const commonTrans = useTranslation('common');
  const {
    value,
    hasError,
    onChange
  } = field;
  const inputId = id || placeholder;
  const errorId = hasError ? `${inputId}-error` : undefined;
  return <input id={inputId} placeholder={focused ? undefined : commonTrans.t(placeholder)} disabled={disabled} sx={styles.input(hasError, disabled)} type="text" value={value || ''} maxLength={maxLength || undefined} name={placeholder} onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} onFocus={focusIn} onBlur={focusOut} data-testid={cy || 'input-field'} aria-invalid={ariaInvalid !== undefined ? ariaInvalid : hasError} aria-errormessage={ariaErrorMessage || errorId} aria-label={ariaLabel} aria-describedby={ariaDescribedBy} aria-required={required} required={required} />;
});
type PropsType = {
  field: FieldState<string | null> | FieldState<string>;
  addSx?: Record<string, unknown>;
  placeholder: string;
  disabled?: boolean;
  margin?: string;
  width?: string;
  maxLength?: number;
  bottomDescription?: string;
  cy?: string;
  ariaInvalid?: boolean;
  ariaErrorMessage?: string;
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  required?: boolean;
};
const Input = ({
  field,
  placeholder,
  disabled = false,
  margin = '0',
  width = '100',
  maxLength = 0,
  bottomDescription = '',
  cy = '',
  addSx = {},
  ariaInvalid,
  ariaErrorMessage,
  id,
  ariaLabel,
  ariaDescribedBy,
  required = false
}: PropsType) => {
  const [focused, setFocused] = useState(false);
  const commonTrans = useTranslation('common');
  const {
    hasError
  } = field;
  const handleFocus = () => setFocused(true);
  const handleBlur = () => {
    void field.validate();
    setFocused(false);
  };
  const hasValue = field.value;
  const displayLabel = focused || !!hasValue;
  const inputId = id || placeholder;
  const errorId = hasError ? `${inputId}-error` : undefined;
  return <div sx={{
    ...styles.inputContainer(hasError, margin, width),
    ...addSx
  }} data-testid={cy ? `${cy}-container` : 'input-container'}>
      <label htmlFor={inputId} sx={styles.label(!!field.error, displayLabel, disabled)} data-testid={cy ? `${cy}-label` : 'input-label'}>
        {displayLabel ? commonTrans.t(placeholder) : ''}
      </label>

      <InternalInput field={field} placeholder={placeholder} disabled={disabled} focused={focused} focusIn={handleFocus} focusOut={handleBlur} maxLength={maxLength} cy={cy} ariaInvalid={ariaInvalid} ariaErrorMessage={ariaErrorMessage} id={inputId} ariaLabel={ariaLabel} ariaDescribedBy={ariaDescribedBy} required={required} />

      {bottomDescription && <InputBottomDescription description={bottomDescription} length={field.value?.length || 0} maxLength={maxLength} />}

      {hasError && <p sx={styles.error(hasError)} data-testid={cy ? `${cy}-error` : 'input-error'} id={errorId} role="alert">
          {hasError && commonTrans.t(`${field.error}`)}
        </p>}
    </div>;
};
export default observer(Input);